import { Code as CodeIcon, Info as InfoIcon } from '@mui/icons-material';
import { Box, Button, Fab } from '@mui/material';
import { Alert } from '@mui/material';
import dayjs from 'dayjs';
import { cloneDeep, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { GCFProductsContext } from 'src/components/context';
import { EditComponentPageContext } from 'src/components/context/components/EditComponentPageContext';
import { ErrorInfo } from 'src/components/errors/ErrorInfo';
import { FullGcfComponentSection } from 'src/components/gcfComponentSection';
import { Modal, SimpleDialog } from 'src/components/modal';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { FrameRangeValidationContext } from 'src/components/widget/lineupWidget/editProductPanel/components/productAnimationAccordion/modules/frameRangeSelector/FrameRangeValidationContext';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import { UpdateComponentSettings } from 'src/features/components/editComponentPage/modules/updateComponentSettings/UpdateComponentSettings';
import { useModal, useParamQuery } from 'src/hooks';
import {
  useGetAllAssets,
  useGetCCAssets,
  useGetComponent,
  useGetProducts,
  useGetThemes,
  useUpdateComponent,
  useUploadCCAsset,
} from 'src/hooks/api';
import { useGetPublication } from 'src/hooks/api/publication/useGetPublication';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useApiErrorNavigator, useNavigateBlocker } from 'src/hooks/navigation';
import {
  ComponentTypes,
  DynamicSectionEvents,
  IComponent,
  IExpiredStatus,
  IGcfComponentConfig,
  IGcfProductList,
  IGcfProductListGroup,
  IGcfProductListProduct,
  IGcfProductListProductImage,
  IGcfProductListProductSize,
  IGcfProductListProductSizeVariation,
  IProduct,
  IStatus,
  PublicationTypes,
} from 'src/interfaces';
import {
  generateAssemblyProductFilters,
  getDaysFromExpiryMessage,
  getExpiryStatusMessage,
  lang,
  useUserRole,
} from 'src/utils';
import { filterGcfProducts, getSectionPreviewData } from 'src/utils/gcf-helper';
import {
  saveComponentAsDraftEvent,
  saveComponentReadyToUseEvent,
} from 'src/utils/google-tag-manager';
import $schema from 'src/utils/schema-helper';

import { StyledWarningIcon } from '../editComponentPage/forms/FormWrapper.styled';
import {
  cleanupComponentLineup,
  findComponentAssets,
  getAssetIdsByProductCodes,
  hasEmptyMessage,
  isComponentDetailsChanged,
  isDy,
  isNonEmptyString,
  sanitizeRichTextInObject,
  shouldDisplayInfoBox,
} from './EditComponentPage.helper';
import {
  ActionButtonsContainer,
  ComponentEditContainer,
  ComponentEditContentContainer,
  ComponentEditSideMenuContainer,
  ComponentNameText,
  HeaderContainer,
} from './EditComponentPage.styled';
import { CodSchema } from './forms/cod/CODForm.schema';
import { FormSelector } from './forms/FormSelector';
import { ISize } from './forms/FormSelector.interface';
import { useEditComponentPage } from './hooks/EditComponentPage.hook';
import { InfoBox } from './modules/InfoBox';
import { renderSaveComponentSubtitle } from './modules/SaveModal';

/**
 * Extracts all product codes from productList configuration
 */
export const extractProductCodesFromProductList = (
  productList: IGcfProductList,
): string[] => {
  const productCodes: Set<string> = new Set();

  if (!productList?.groups) {
    return [];
  }

  Object.values(productList.groups).forEach((group: IGcfProductListGroup) => {
    if (!group?.products) return;

    if (group?.products) {
      Object.values(group.products).forEach(
        (product: IGcfProductListProduct) => {
          if (product.productCode) {
            productCodes.add(product.productCode);
          }

          if (product.sizes) {
            Object.values(product.sizes).forEach(
              (size: IGcfProductListProductSize) => {
                if (size.variations) {
                  Object.values(size.variations).forEach(
                    (variation: IGcfProductListProductSizeVariation) => {
                      if (variation.productCode) {
                        productCodes.add(variation.productCode);
                      }
                    },
                  );
                }
              },
            );
          }

          if (product.bundleImages) {
            product.bundleImages.forEach(
              (bundleImage: IGcfProductListProductImage) => {
                if (bundleImage.productCode) {
                  productCodes.add(bundleImage.productCode);
                }
              },
            );
          }
        },
      );
    }
    if (group?.backups) {
      Object.values(group.backups).forEach((backup: IGcfProductListProduct) => {
        if (backup.productCode) {
          productCodes.add(backup.productCode);
        }

        if (backup.sizes) {
          Object.values(backup.sizes).forEach(
            (size: IGcfProductListProductSize) => {
              if (size.variations) {
                Object.values(size.variations).forEach(
                  (variation: IGcfProductListProductSizeVariation) => {
                    if (variation.productCode) {
                      productCodes.add(variation.productCode);
                    }
                  },
                );
              }
            },
          );
        }

        if (backup.bundleImages) {
          backup.bundleImages.forEach(
            (bundleImage: IGcfProductListProductImage) => {
              if (bundleImage.productCode) {
                productCodes.add(bundleImage.productCode);
              }
            },
          );
        }
      });
    }
  });

  return Array.from(productCodes);
};

const stripTransientConfigFields = (
  data?: Partial<IGcfComponentConfig>,
): Partial<IGcfComponentConfig> | undefined => {
  if (!data) {
    return data;
  }

  const sanitizedData = cloneDeep(data);

  if (sanitizedData.ads) {
    delete sanitizedData.ads.showPlaceholder;
  }

  return sanitizedData;
};

/**
 * Checks if any products used in the component have been modified after publish
 */
export const hasComponentProductsModifiedAfterPublish = (
  productList: IGcfProductList,
  allProducts: IProduct[],
  lastPublishedAt?: string,
): boolean => {
  if (!lastPublishedAt || !allProducts?.length || !productList) {
    return false;
  }

  const usedProductCodes = extractProductCodesFromProductList(productList);

  if (usedProductCodes.length === 0) {
    return false;
  }

  // Filter products to only those used in the component
  const usedProducts = allProducts.filter((product) =>
    usedProductCodes.includes(product.product_code?.toString()),
  );

  return hasAnyProductModifiedAfterPublish(usedProducts, lastPublishedAt);
};

export const hasAnyProductModifiedAfterPublish = (
  products: IProduct[],
  lastPublishedAt?: string,
): boolean => {
  if (!lastPublishedAt || !products?.length) {
    return false;
  }

  return products.some((product) => {
    const parsedLastPublishedDate = dayjs(lastPublishedAt);
    const isProductModifiedAfterPublish =
      product.modified_at &&
      dayjs(product.modified_at).isAfter(parsedLastPublishedDate);

    const isVariationModifiedAfterPublish = product.productVariations?.some(
      (variation) =>
        variation.modified_at &&
        dayjs(variation.modified_at).isAfter(parsedLastPublishedDate),
    );

    // Return true if either the product or any variation is modified after publish
    return isProductModifiedAfterPublish || isVariationModifiedAfterPublish;
  });
};

export const EditComponentPage = () => {
  const form = useForm<any>({ mode: 'onChange' });
  const { formState } = form;
  const updateComponent = useUpdateComponent();
  const { data: sbAssets = [] } = useGetAllAssets();
  const { data: ccAssets = [], isLoading: isCCAssetsLoading } =
    useGetCCAssets();
  const uploadCCAsset = useUploadCCAsset();
  const { data: themes = [] } = useGetThemes();
  const { componentId = '' } = useParams();
  const {
    data: componentDetails,
    error,
    isLoading,
  } = useGetComponent(componentId, undefined, true);

  const { data } = useGetPublication(PublicationTypes.PRODUCTS);

  const lastPublished = data?.last_published_at;

  const assetRevision = useMemo(
    () =>
      sbAssets.reduce((max, asset) => {
        const match = asset.thumbnailUrl?.match(/\?t=(\d+)/);

        return match ? Math.max(max, parseInt(match[1], 10)) : max;
      }, 0),
    [sbAssets],
  );

  const { activeParams } = useParamQuery();
  const { isViewOnly } = useUserRole();
  const [componentData, setComponentData] = useState<IComponent | undefined>();
  const [size, setSize] = useState<ISize>();
  const [frameRangeErrors, setFrameRangeErrors] = useState<
    Record<string, boolean>
  >({});

  const setFrameRangeError = (key: string, hasError: boolean) => {
    setFrameRangeErrors((prev) => ({ ...prev, [key]: hasError }));
  };

  const hasAnyFrameRangeError = Object.values(frameRangeErrors).some(Boolean);

  const [hasTriedSaving, setHasTriedSaving] = useState(false);
  const [saveToken, setSaveToken] = useState<object>({});

  const navigate = useNavigate();

  useEffect(() => {
    setComponentData(componentDetails);
  }, [isLoading, componentDetails]);

  const deletedProductAlertModal = useModal();
  const globalThemeAlertModal = useModal();
  const productPublicationAlertModal = useModal();

  const [productAlertShown, setProductAlertShown] = useState(false);

  const {
    gcfProducts,
    componentConfig,
    projectConfig,
    showComponentSettings,
    onShowComponentSettings,
    onChangedComponentConfig,
    showConfig,
    onConfigPanelHandler,
    onChangeGCFProductStatus,
    contextValues,
    setGcfProducts,
    orderType,
    setOrderType,
  } = useEditComponentPage(componentDetails);

  // Generate product filters for GcfComponentSection preview
  const { productFilters, productVariationFilters } = useMemo(() => {
    if (!componentDetails || !componentConfig) {
      return { productFilters: [], productVariationFilters: [] };
    }

    const currentComponent = {
      ...componentDetails,
      config: componentConfig,
    };

    return generateAssemblyProductFilters([], [currentComponent]);
  }, [componentDetails, componentConfig]);

  const hasProductCategories =
    componentDetails?.product_category_id &&
    componentDetails.product_category_id.length > 0;

  const { data: products = [] } = useGetProducts(
    hasProductCategories
      ? [
          `product_category_id=${componentDetails.product_category_id
            .sort()
            .join('|')}`,
        ]
      : [],
    [],
    {
      enabled: hasProductCategories,
    },
  );

  // Show warning if any product used in the component has been modified after publish
  const showWarning = useMemo(() => {
    return hasComponentProductsModifiedAfterPublish(
      contextValues?.componentDetails?.config?.productList,
      products,
      lastPublished,
    );
  }, [contextValues, products, lastPublished]);

  useNavigateBlocker(() =>
    isComponentDetailsChanged(
      componentDetails,
      contextValues.componentDetails,
      projectConfig,
    ),
  );
  useApiErrorNavigator(error);

  const expiringInDays = getDaysFromExpiryMessage(
    getExpiryStatusMessage(componentDetails?.end_date),
  );

  const displayInfoBox = shouldDisplayInfoBox(
    componentDetails?.component_type?.name,
    componentConfig?.productsSource,
  );

  const isActiveThemeValid = useCallback(
    (themeId?: number) => {
      if (!themeId || isLoading) {
        return true;
      }
      const category = themes.find((item) => item.id === themeId)?.config
        ?.category;

      if (isEmpty(category)) {
        return false;
      }

      for (let prop in category) {
        if (category.hasOwnProperty(prop)) {
          return !(
            isEmpty(category[prop].label) ||
            isEmpty(category[prop].nutrition) ||
            isEmpty(category[prop].price)
          );
        }
      }

      return true;
    },
    [isLoading, themes],
  );

  const handleConfigSave = async (
    data?: Partial<IGcfComponentConfig>,
    status: IStatus = IStatus.DRAFT,
  ) => {
    try {
      const sanitizedData = stripTransientConfigFields(data);

      /* Extract assets in the component config */
      const { assets: componentAssets, missingAssets } = findComponentAssets(
        sanitizedData,
        ccAssets,
      );

      const { productList, productVariationCodes } = filterGcfProducts(
        products,
        sanitizedData?.productList,
      );

      /* Collect asset IDs from product variations that are used in the component config */
      const productAssetIds = getAssetIdsByProductCodes(
        products,
        productVariationCodes,
      );

      let uploadAssetIds: number[] = [];

      if (!isEmpty(missingAssets)) {
        uploadAssetIds = await uploadCCAsset.mutateAsync(missingAssets);
      }

      const assetIds = Array.from(
        new Set([
          ...componentAssets.map((item) => item.id),
          ...uploadAssetIds,
          ...productAssetIds,
        ]),
      );

      const commonPayload = {
        ...contextValues.componentDetails,
        config: {
          ...sanitizedData,
          productList: productList,
        },
        status,
        product_variation_product_code: productVariationCodes,
        asset_id: assetIds,
        narrative: (
          narrativeGenerators[COLLECTIONS.COMPONENTS][USER_ACTIONS.UPDATE] as (
            compName: string,
          ) => string
        )(contextValues.componentDetails.name),
      };

      await updateComponent.mutateAsync(commonPayload);

      if (status === IStatus.READY) {
        if (showWarning) {
          productPublicationAlertModal.onOpenModal();
        } else {
          /* Delay navigation to ensure original and updated data are synced, preventing incorrect blocking behavior */
          setTimeout(() => {
            navigate(PATHS.COMPONENTS);
          }, 500);
        }
        saveComponentReadyToUseEvent(Number(componentId));
        expiringInDays &&
          Snackbar.warning(
            `${componentDetails && componentDetails.name} ${
              lang.componentExpiringIn
            } ${expiringInDays} ${
              expiringInDays === 1 ? lang.day : lang.day + 's'
            }`,
          );
        Snackbar.success(
          `${componentDetails && componentDetails.name} ${
            lang.successfullySaveAsReady
          }`,
        );
      } else {
        saveComponentAsDraftEvent(Number(componentId));
        Snackbar.success(
          `${componentDetails && componentDetails.name} ${
            lang.successfullySavedAsDraft
          }`,
        );
      }
      setGcfProducts({});
    } catch (error) {
      console.error(lang.failedToSaveAsDraft);
    }
  };

  const onSaveConfig = (data: any) => {
    handleConfigSave(data, IStatus.READY);
  };

  const onSaveAsDraft = () => {
    handleConfigSave(componentConfig, IStatus.DRAFT);
  };
  const saveComponentModal = useModal();
  useEffect(() => {
    if (formState?.isSubmitSuccessful) {
      saveComponentModal.onCloseModal();
    }
    if (!formState?.isValid && !isEmpty(formState?.errors)) {
      if (!hasShownValidationError.current) {
        hasShownValidationError.current = true;

        Snackbar.error(lang.failedToSave);
      }
    }
  }, [
    formState?.isValid,
    formState?.errors,
    formState?.isSubmitSuccessful,
    saveComponentModal,
  ]);

  const isExpired =
    getExpiryStatusMessage(componentDetails?.end_date) ===
    IExpiredStatus.expired;

  useEffect(() => {
    if (
      contextValues.componentDetails.component_type?.name ===
      ComponentTypes.COMBO_STYLE
    ) {
      const isThemeValid = isActiveThemeValid(
        contextValues.componentDetails.theme_id,
      );

      if (isThemeValid && globalThemeAlertModal.open) {
        globalThemeAlertModal.onCloseModal();
      } else if (!isThemeValid && !globalThemeAlertModal.open) {
        globalThemeAlertModal.onOpenModal();
      }
    }
  }, [
    contextValues.componentDetails.theme_id,
    contextValues.componentDetails.component_type,
    globalThemeAlertModal,
    isActiveThemeValid,
  ]);

  useEffect(() => {
    setProductAlertShown(false);
  }, [componentId]);

  useEffect(() => {
    if (
      !productAlertShown &&
      componentConfig !== undefined &&
      'productList' in componentConfig
    ) {
      cleanupComponentLineup(componentConfig, products).then(
        (hasRemovedProducts) => {
          if (hasRemovedProducts && !productAlertShown) {
            onChangedComponentConfig(componentConfig);
            !productAlertShown && deletedProductAlertModal.onOpenModal();
            setProductAlertShown(true);
          }
        },
      );
    }
  }, [
    componentConfig,
    productAlertShown,
    products,
    onChangedComponentConfig,
    deletedProductAlertModal,
  ]);

  const onUpdateComponentSettings = async (settings: Partial<IComponent>) => {
    try {
      await updateComponent.mutateAsync({
        ...componentDetails,
        ...settings,
      });
      Snackbar.success(
        `${settings && settings.name} ${lang.successfullySavedAsDraft}`,
      );
    } catch (error) {
      console.error(lang.failedToSaveAsDraft);
    }

    onShowComponentSettings();
  };
  const hasShownValidationError = useRef(false);
  const handleGlobalThemeAlertClose = () => {
    globalThemeAlertModal.onCloseModal();

    contextValues.handleChangeComponentDetails(
      [
        componentDetails?.theme_id,
        themes.find((item) => item.id === componentDetails?.theme_id),
      ],
      ['theme_id', 'theme'],
    );
  };
  const handleSave = async (formData: any) => {
    setHasTriedSaving(true);
    setSaveToken({});

    const formType = contextValues.componentDetails.component_type?.name;

    if (formType === 'cod' || formType === 'cod-thank-you') {
      if (formType === 'cod') {
        const hasCodFullBanner = isNonEmptyString(
          contextValues?.componentDetails?.config?.codSettings?.confirmSetting
            ?.header?.review?.banner?.images?.largeImage?.background,
        );
        const hasCodHalfBanner = isNonEmptyString(
          contextValues?.componentDetails?.config?.codSettings?.confirmSetting
            ?.header?.review?.banner?.images?.smallImage?.background,
        );
        const hasConfirmationDonationLabel = isNonEmptyString(
          contextValues?.componentDetails?.config?.codSettings?.confirmSetting
            ?.total?.donate?.label,
        );
        if (
          hasEmptyMessage(
            sanitizeRichTextInObject(
              contextValues?.componentDetails?.config?.codSettings?.codSetting,
            ),
          ) ||
          !hasCodFullBanner ||
          !hasCodHalfBanner ||
          !hasConfirmationDonationLabel
        ) {
          //add toast if sanitized message or message without any html is empty
          Snackbar.error(lang.failedToSave);

          return;
        }
        const isValid = $schema.validate(formData, CodSchema);
        if (!isValid) {
          if (!hasShownValidationError.current) {
            hasShownValidationError.current = true;
            Snackbar.error(lang.failedToSave);
          }

          return;
        }
      }

      if (formType === 'cod-thank-you') {
        const config = componentConfig as any;

        const backgrounds = [
          config?.unknownCustomer?.background,
          config?.knownCustomer?.background,
        ];

        const mopBackground = config?.knownCustomer?.mop?.background;

        // Only validate MOP background if the field exists/enabled
        if (mopBackground != null) {
          backgrounds.push(mopBackground);
        }

        // Validate Ads backgrounds when Ads is enabled
        if (config?.ads?.isAdsEnabled) {
          backgrounds.push(
            config?.ads?.content?.unknownCustomer?.background,
            config?.ads?.content?.knownCustomer?.background,
          );

          const adsMopBackground =
            config?.ads?.content?.knownCustomer?.mop?.background;
          if (adsMopBackground != null) {
            backgrounds.push(adsMopBackground);
          }
        }

        const isValid = backgrounds.every(isNonEmptyString);

        if (!isValid) {
          if (!hasShownValidationError.current) {
            hasShownValidationError.current = true;
            Snackbar.error(lang.failedToSave);
          }

          return;
        }
      }
    } else {
      const config = contextValues?.componentDetails?.config || {};

      const hasTopLevelBackground = isNonEmptyString(config.background);
      const isMobileUserEnabled = !!config.mop;
      const hasMobileBackground = isNonEmptyString(config.mop?.background);

      const isValid =
        hasTopLevelBackground && (!isMobileUserEnabled || hasMobileBackground);

      if (!isValid) {
        if (!hasShownValidationError.current) {
          hasShownValidationError.current = true;
          Snackbar.error(lang.failedToSave);
        }

        return;
      }
    }

    saveComponentModal.onCloseModal();
    await handleConfigSave(componentConfig, IStatus.READY);
  };

  const onProductPublicationModalClose = (pathName: string) => {
    productPublicationAlertModal.onCloseModal();
    navigate(pathName);
  };

  if (!componentId || !componentDetails) return null;

  const isCod =
    componentConfig && componentDetails?.component_type?.name === 'cod';
  const isValid = isCod && $schema.validate(componentConfig, CodSchema);
  const basePreviewData = getSectionPreviewData(
    componentDetails?.component_type?.name as ComponentTypes,
    size
      ? DynamicSectionEvents.COD_CONFIRMATION_OPEN
      : DynamicSectionEvents.COD_ORDER_OPEN,
    orderType,
  );

  const previewData = basePreviewData
    ? {
        ...basePreviewData,
        sectionConfig: {
          ...(basePreviewData as any).sectionConfig,
          isCustomerKnown: contextValues.isCustomerKnown,
        },
      }
    : null;

  return (
    <EditComponentPageContext.Provider value={contextValues}>
      <FrameRangeValidationContext.Provider
        value={{ setFrameRangeError, hasAnyFrameRangeError }}>
        <GCFProductsContext.Provider
          value={{
            value: gcfProducts,
            onChangeGCFProductStatus,
          }}>
          {isCod && !isValid ? (
            <ErrorInfo
              title={`Oh no! We can’t read your data source because it is incompatible and not supported.`}
            />
          ) : (
            <ComponentEditContainer>
              <ComponentEditSideMenuContainer data-testid="edit-component-page-side-menu">
                <FormSelector
                  selectedComponent={contextValues.componentDetails}
                  setSize={setSize}
                  hasTriedSaving={hasTriedSaving}
                  saveToken={saveToken}
                  saveComponentConfig={onSaveConfig}
                  setConfig={onChangedComponentConfig}
                  setOrderType={setOrderType}
                  form={form}
                />
              </ComponentEditSideMenuContainer>
              <ComponentEditContentContainer>
                <HeaderContainer>
                  <Box flex={1} overflow={'hidden'}>
                    <Breadcrumbs
                      items={[
                        {
                          label: lang.components,
                          link: PATHS.COMPONENTS,
                        },
                        {
                          label: lang.edit,
                        },
                      ]}
                    />

                    <CustomTooltip
                      placement="bottom"
                      title={componentDetails?.name}
                      overflowLineNumber={1}
                      width={500}>
                      <ComponentNameText variant="h3" component="h1">
                        {componentDetails?.name}
                      </ComponentNameText>
                    </CustomTooltip>
                  </Box>
                  <ActionButtonsContainer spacing={2} direction="row">
                    {isViewOnly ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          margin: 3,
                        }}>
                        <ViewOnlyAlert />
                      </Box>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => onShowComponentSettings()}
                          startIcon={<InfoIcon />}>
                          {lang.info}
                        </Button>

                        <Button
                          data-testid="save-as-draft-button"
                          variant="text"
                          onClick={onSaveAsDraft}
                          disabled={
                            componentDetails.status === IStatus.READY ||
                            isCCAssetsLoading
                          }>
                          {lang.saveAsDraft}
                        </Button>

                        {!isViewOnly && (
                          <Button
                            variant="contained"
                            data-testid="save-button"
                            onClick={() => {
                              hasShownValidationError.current = false;
                              saveComponentModal.onOpenModal();
                            }}
                            disabled={
                              componentDetails?.status === IStatus.READY ||
                              hasAnyFrameRangeError ||
                              isCCAssetsLoading
                            }>
                            {lang.save}
                          </Button>
                        )}
                      </>
                    )}
                  </ActionButtonsContainer>
                </HeaderContainer>

                <InfoBox visible={displayInfoBox}>
                  The products shown in this preview are examples only.
                  {isDy(componentConfig?.productsSource) &&
                    ` Product categories won’t apply to the dynamic products`}
                </InfoBox>
                <ScaleContainer enableScaleControls>
                  {componentConfig && componentData && (
                    <FullGcfComponentSection
                      key={assetRevision}
                      assemblyComponents={[
                        {
                          component: {
                            theme: contextValues.componentDetails.theme?.name,
                            type: componentDetails?.component_type?.name,
                            config: componentConfig,
                            name: componentDetails?.name,
                            width: componentDetails?.width,
                            height: componentDetails?.height,
                          },
                        },
                      ]}
                      height={
                        size?.height ? size.height : componentDetails.height
                      }
                      width={size?.width ? size.width : componentDetails.width}
                      previewData={previewData}
                      containerStyle={{
                        borderWidth: '3px',
                        borderStyle: 'solid',
                        borderColor: 'brand.darkGrey',
                      }}
                      overrideQuerySetting={{
                        staleTime: Infinity,
                        keepPreviousData: true,
                      }}
                      productFilters={productFilters}
                      productVariationFilters={productVariationFilters}
                      skipProductsApiOnEmptyFilters={true}
                    />
                  )}
                </ScaleContainer>
                <Modal
                  fullScreen={false}
                  children={
                    <UpdateComponentSettings
                      handleClose={onShowComponentSettings}
                      componentDetails={contextValues.componentDetails || null}
                      componentConfig={componentConfig}
                      onUpdate={(settings) =>
                        onUpdateComponentSettings(settings)
                      }
                      products={products}
                    />
                  }
                  open={showComponentSettings}
                  onClose={onShowComponentSettings}
                />
                <SimpleDialog
                  open={saveComponentModal.open}
                  cancelText={lang.cancel}
                  confirmText={lang.save}
                  title={lang.saveComponentModal.title}
                  subTitle={
                    !isExpired
                      ? renderSaveComponentSubtitle(
                          lang,
                          componentDetails.usable_as_override,
                          showWarning,
                        )
                      : ''
                  }
                  onClose={saveComponentModal.onCloseModal}
                  onClickCancel={saveComponentModal.onCloseModal}
                  modalSize="sm"
                  onClickConfirm={async () => {
                    hasShownValidationError.current = false;

                    const isValid = await form.trigger(); // ✅ manual validation
                    if (!isValid) {
                      if (!hasShownValidationError.current) {
                        hasShownValidationError.current = true;
                        Snackbar.error(lang.failedToSave);
                      }
                      saveComponentModal.onCloseModal(); // ✅ close modal on validation fail

                      return;
                    }

                    const formData = form.getValues(); // ✅ get valid form data
                    await handleSave(formData); // ✅ call actual save logic
                    saveComponentModal.onCloseModal(); // ✅ close modal on success
                  }}
                  disabled={isExpired}>
                  {isExpired && (
                    <Alert severity="warning" icon={<StyledWarningIcon />}>
                      {lang.expiredComponent}
                    </Alert>
                  )}
                </SimpleDialog>
                <Modal
                  fullScreen={false}
                  children={
                    <UpdateComponentSettings
                      handleClose={onShowComponentSettings}
                      componentDetails={contextValues.componentDetails || null}
                      componentConfig={componentConfig}
                      onUpdate={(settings) =>
                        onUpdateComponentSettings(settings)
                      }
                      products={products}
                    />
                  }
                  open={showComponentSettings}
                  onClose={onShowComponentSettings}
                />

                <SimpleDialog
                  open={deletedProductAlertModal.open}
                  modalSize="sm"
                  title={lang.componentSaveModal.title}
                  subTitle={lang.componentSaveModal.subTitle}
                  confirmText={lang.close}
                  onClickConfirm={deletedProductAlertModal.onCloseModal}
                  onClose={deletedProductAlertModal.onCloseModal}
                />

                <SimpleDialog
                  open={globalThemeAlertModal.open}
                  modalSize="sm"
                  title={lang.headerAccessibilityConfigTitle}
                  subTitle={lang.globalThemeAlertCaption(
                    contextValues.componentDetails.theme?.name,
                  )}
                  confirmText={lang.headerAccessibilityConfigTitle}
                  cancelText={lang.close}
                  disableClose={!isActiveThemeValid(componentDetails?.theme_id)}
                  onClickConfirm={() => {
                    navigate(PATHS.SETTINGS_ACCESSIBILITY);
                  }}
                  onClickCancel={handleGlobalThemeAlertClose}
                  onClose={handleGlobalThemeAlertClose}
                />

                <SimpleDialog
                  open={productPublicationAlertModal.open}
                  modalSize="sm"
                  title={lang.titleTextAfterSave}
                  subTitle={lang.saveComponentModal.publishProductsText}
                  confirmText={lang.goToPublications}
                  cancelText={lang.close}
                  onClickConfirm={() => {
                    onProductPublicationModalClose(PATHS.PRODUCT_PUBLICATIONS);
                  }}
                  onClickCancel={() => {
                    onProductPublicationModalClose(PATHS.COMPONENTS);
                  }}
                  onClose={() => {
                    onProductPublicationModalClose(PATHS.COMPONENTS);
                  }}
                />

                {activeParams?.debug?.[0] && (
                  <>
                    <Fab
                      color="primary"
                      aria-label="add"
                      onClick={onConfigPanelHandler}>
                      <CodeIcon />
                    </Fab>
                    {showConfig && (
                      <textarea
                        disabled
                        style={{
                          position: 'absolute',
                          top: '3.75rem',
                          height: '85vh',
                          width: '18.75rem',
                        }}
                        value={JSON.stringify(componentConfig, null, 2)}
                      />
                    )}
                  </>
                )}
              </ComponentEditContentContainer>
            </ComponentEditContainer>
          )}
        </GCFProductsContext.Provider>
      </FrameRangeValidationContext.Provider>
    </EditComponentPageContext.Provider>
  );
};
